import type { GuideStructure } from '../../api';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useActionCableSubscription } from '~/hooks';

export enum DeepSearchCompletionType {
  MODULE_REPORT = 'module_report',
  GUIDE_STRUCTURE = 'guide_structure',
}

export interface ModuleReportData {
  type: DeepSearchCompletionType;
  moduleTitle: string;
  content: string; // Markdown content
  references?: string[];
}

export interface GuideStructureData extends GuideStructure {
  type: DeepSearchCompletionType.GUIDE_STRUCTURE;
}

export interface DeepSearchCompletionData {
  status: 'completed' | 'error';
  request_id: string;
  data?: ModuleReportData | GuideStructureData;
  error?: string;
}

interface UseDeepSearchCompletionOptions {
  onCompleted?: (requestId: string, data: ModuleReportData | GuideStructureData) => void;
  onError?: (error: string) => void;
}

interface DeepSearchCompletionContextValue {
  isLoading: boolean;
  data: ModuleReportData | GuideStructureData | null;
  error: string | null;
  lastestMessage: DeepSearchCompletionData | null;
  messagesByRequestId: Map<string, DeepSearchCompletionData[]>;
  subscribe: (requestId: string, options?: UseDeepSearchCompletionOptions) => void;
  unsubscribe: (requestId: string) => void;
  unsubscribeAll: () => void;
}

export const DeepSearchCompletionContext = createContext<DeepSearchCompletionContextValue>({
  isLoading: false,
  data: null,
  error: null,
  lastestMessage: null,
  messagesByRequestId: new Map(),
  subscribe: () => {},
  unsubscribe: () => {},
  unsubscribeAll: () => {},
});

export const DeepSearchCompletionProvider = ({ children }: { children: React.ReactNode }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<ModuleReportData | GuideStructureData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subscriptions, setSubscriptions] = useState<Record<string, UseDeepSearchCompletionOptions>>({});
  const [lastestMessage, setLastestMessage] = useState<DeepSearchCompletionData | null>(null);
  const [messagesByRequestId, setMessagesByRequestId] = useState<Map<string, DeepSearchCompletionData[]>>(new Map());

  const unsubscribe = useCallback((requestId: string) => {
    setSubscriptions((prev) => {
      const newSubscriptions = { ...prev };
      delete newSubscriptions[requestId];
      return newSubscriptions;
    });
  }, []);

  const unsubscribeAll = useCallback(() => {
    setSubscriptions({});
  }, []);

  const handleReceived = useCallback(
    (message: DeepSearchCompletionData) => {
      const subscription = subscriptions[message.request_id];
      setMessagesByRequestId((prev) => {
        const newMessages = new Map(prev);
        if (newMessages.has(message.request_id)) {
          newMessages.get(message.request_id)?.push(message);
        } else {
          newMessages.set(message.request_id, [message]);
        }
        return newMessages;
      });
      setLastestMessage(message);

      if (message.status === 'completed' && message.data) {
        setData(message.data);
        setIsLoading(false);
        if (subscription.onCompleted) {
          subscription.onCompleted(message.request_id, message.data);
        }
      } else if (message.status === 'error') {
        setError(message.error || 'Unknown error');
        setIsLoading(false);
        if (subscription.onError) {
          subscription.onError(message.error || 'Unknown error');
        }
      }
    },
    [subscriptions],
  );

  useActionCableSubscription(
    {
      channel: 'DeepSearch::CompletionChannel',
    },
    {
      received: (message: DeepSearchCompletionData) => {
        handleReceived(message);
      },
    },
    {
      isEnabled: true,
    },
  );

  const subscribe = useCallback((requestId: string, options?: UseDeepSearchCompletionOptions) => {
    setSubscriptions((prev) => ({ ...prev, [requestId]: options }));
  }, []);

  const value = useMemo(
    () => ({
      isLoading,
      data,
      error,
      lastestMessage,
      messagesByRequestId,
      subscribe,
      unsubscribe,
      unsubscribeAll,
    }),
    [isLoading, data, error, lastestMessage, messagesByRequestId, subscribe, unsubscribe, unsubscribeAll],
  );

  return <DeepSearchCompletionContext.Provider value={value}>{children}</DeepSearchCompletionContext.Provider>;
};

export const useDeepSearchCompletion = () => {
  const context = useContext(DeepSearchCompletionContext);
  if (!context) {
    throw new Error('useDeepSearchCompletion must be used within a DeepSearchCompletionProvider');
  }
  return context;
};
